import { Sparkles, Crown, Check, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import { tr } from "@/utils/i18n";

/**
 * Full-width hero-top banner promoting the 30-day free Premium trial.
 * Sales-oriented: hits users the second they land, before the goal form.
 * Clicking any CTA navigates to /subscription where the LAUNCH30 promo is available.
 */
export function TrialBanner({ onCTAClick }) {
  const { language } = useLanguage();

  const copy = {
    ribbon: tr(language, "LIMITED-TIME LAUNCH OFFER", "OFERTA DE LANÇAMENTO POR TEMPO LIMITADO", "OFERTA DE LANZAMIENTO POR TIEMPO LIMITADO"),
    headline: tr(
      language,
      "Try Premium FREE for 30 Days",
      "Experimente Premium GRÁTIS por 30 Dias",
      "Prueba Premium GRATIS por 30 Días"
    ),
    subhead: tr(
      language,
      "Cancel anytime · No commitment · No penalty",
      "Cancele quando quiser · Sem compromisso · Sem multa",
      "Cancela cuando quieras · Sin compromiso · Sin penalización"
    ),
    b1: tr(language, "30% OFF peptides at Summit", "30% OFF peptídeos na Summit", "30% OFF péptidos en Summit"),
    b2: tr(language, "Unlimited AI-generated cycles", "Ciclos IA ilimitados", "Ciclos IA ilimitados"),
    b3: tr(language, "Custom peptide protocols", "Protocolos personalizados", "Protocolos personalizados"),
    b4: tr(language, "Share, PDF export, collaborators", "Compartilhar, PDF, colaboradores", "Compartir, PDF, colaboradores"),
    b5: tr(language, "1-on-1 consultation booking", "Agendamento de consulta 1-a-1", "Reserva de consulta 1-a-1"),
    disclaimer: tr(
      language,
      "Credit or debit card on file required. You won't be charged for 30 days. After that, standard monthly pricing applies unless you cancel.",
      "Cartão de crédito ou débito necessário. Você não será cobrado por 30 dias. Após isso, aplica-se o preço mensal padrão, exceto se cancelar.",
      "Se requiere tarjeta de crédito o débito. No se te cobrará durante 30 días. Después de eso, aplica el precio mensual estándar a menos que canceles."
    ),
    cta: tr(language, "Start 30-Day Free Trial", "Começar Teste Grátis de 30 Dias", "Comenzar Prueba Gratis de 30 Días"),
    secondaryHint: tr(language, "or scroll down to try the free version", "ou role para baixo para experimentar a versão grátis", "o desplázate para probar la versión gratuita"),
  };

  return (
    <section
      className="relative overflow-hidden border-b border-amber-500/30 bg-gradient-to-r from-amber-500/15 via-yellow-500/10 to-cyan-500/15"
      data-testid="trial-banner"
    >
      {/* Subtle animated glow */}
      <div className="absolute -top-16 left-1/4 w-72 h-72 bg-amber-400/20 rounded-full blur-3xl animate-pulse" />
      <div className="absolute -bottom-16 right-1/4 w-72 h-72 bg-cyan-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <div className="flex flex-col lg:flex-row items-center gap-6">
          {/* Left: headline + benefits */}
          <div className="flex-1 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/20 border border-red-500/40 mb-3">
              <Zap className="w-3 h-3 text-red-400" />
              <span className="text-xs font-bold text-red-300 uppercase tracking-wider">{copy.ribbon}</span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white mb-2">
              <Crown className="inline-block w-8 h-8 text-amber-400 mr-2 mb-1" />
              <span className="bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-400 bg-clip-text text-transparent">
                {copy.headline}
              </span>
            </h2>

            <p className="text-lg sm:text-xl text-amber-200 font-semibold mb-4" data-testid="trial-subhead">
              {copy.subhead}
            </p>

            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-zinc-200 max-w-2xl mx-auto lg:mx-0 mb-4">
              {[copy.b1, copy.b2, copy.b3, copy.b4, copy.b5].map((benefit) => (
                <li key={benefit} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-400 shrink-0 mt-0.5" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>

            <p className="text-xs text-zinc-400 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
              {copy.disclaimer}
            </p>
          </div>

          {/* Right: CTA */}
          <div className="flex flex-col items-center gap-3 shrink-0 lg:min-w-[280px]">
            <Button
              onClick={onCTAClick}
              size="lg"
              className="w-full h-14 px-8 bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-black font-black text-base sm:text-lg rounded-xl shadow-lg shadow-amber-500/30 hover:shadow-amber-400/40 hover:scale-105 transition-all"
              data-testid="trial-banner-cta"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              {copy.cta}
            </Button>
            <span className="text-xs text-zinc-500 text-center italic">
              ↓ {copy.secondaryHint}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
